import { useState } from "react";
import { Play, Instagram } from "lucide-react";
import { FILMS, BRAND } from "../../content/site";
import { Dialog, DialogContent, DialogTitle } from "../ui/dialog";
import { SectionHeading, Reveal } from "./Motion";

const reelCode = (url) => url?.match(/instagram\.com\/(?:reel|reels|p)\/([\w-]+)/)?.[1];
const playable = (f) => !!(f.youtubeId || reelCode(f.instagramUrl));

const FilmCard = ({ f, i, onPlay }) => {
  const wide = f.format === "wide";
  const canPlay = playable(f);
  const handle = () => {
    if (canPlay) onPlay(f);
    else window.open(f.instagramUrl || BRAND.instagramUrl, "_blank", "noopener");
  };
  return (
    <Reveal delay={i * 0.1} className={wide ? "md:col-span-2 md:row-span-2" : ""}>
      <button onClick={handle} className="group relative block h-full w-full overflow-hidden rounded-[28px] text-left" data-testid={`film-card-${f.id}`}>
        <div className={`relative h-full w-full ${wide ? "aspect-[4/3] md:aspect-auto md:min-h-[560px]" : "aspect-[4/5] md:aspect-auto md:min-h-[268px]"}`}>
          <img src={f.poster} alt={f.title} loading="lazy" className="absolute inset-0 h-full w-full object-cover transition-transform duration-[1400ms] ease-out group-hover:scale-105" />
          <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/10 to-transparent" />
          <span className="absolute left-1/2 top-1/2 grid h-20 w-20 -translate-x-1/2 -translate-y-1/2 place-items-center rounded-full border border-white/60 bg-white/15 text-white backdrop-blur-md transition-transform duration-500 group-hover:scale-110">
            {canPlay ? <Play className="ml-1 h-7 w-7 fill-white" /> : <Instagram className="h-7 w-7" />}
          </span>
          <div className="absolute bottom-0 inset-x-0 p-6">
            <p className="text-[11px] uppercase tracking-[0.3em] text-peach">{f.subtitle}</p>
            <h3 className="mt-1 font-serif text-3xl text-white">{f.title}</h3>
            <p className="mt-1 text-xs text-white/85">{canPlay ? "Tap to play" : "Watch on Instagram"}</p>
          </div>
        </div>
      </button>
    </Reveal>
  );
};

export const Films = () => {
  const [playing, setPlaying] = useState(null);
  return (
    <section id="films" className="relative section-pad" data-testid="films-section">
      <div className="mx-auto max-w-[1300px]">
        <SectionHeading eyebrow="Films & Reels" title="Watch the" script="love unfold" testid="films-heading" />
        <div className="mt-14 grid gap-5 md:grid-cols-3 md:grid-rows-2">
          {FILMS.map((f, i) => (
            <FilmCard key={f.id} f={f} i={i} onPlay={setPlaying} />
          ))}
        </div>
        <Reveal className="mt-10 flex justify-center">
          <a href={BRAND.instagramUrl} target="_blank" rel="noopener noreferrer" className="btn-ghost" data-testid="films-instagram-link">
            <Instagram className="h-4 w-4" /> More reels on {BRAND.instagram}
          </a>
        </Reveal>
      </div>
      <Dialog open={!!playing} onOpenChange={(o) => !o && setPlaying(null)}>
        <DialogContent className={`border-none p-0 ${playing?.youtubeId ? "max-w-4xl bg-black" : "w-[92vw] max-w-[420px] overflow-hidden rounded-2xl bg-white"}`} data-testid="film-dialog" data-lenis-prevent>
          <DialogTitle className="sr-only">{playing?.title}</DialogTitle>
          {playing?.youtubeId && (
            <div className="aspect-video w-full">
              <iframe className="h-full w-full" src={`https://www.youtube.com/embed/${playing.youtubeId}?autoplay=1&rel=0`} title={playing.title} allow="autoplay; encrypted-media; picture-in-picture" allowFullScreen />
            </div>
          )}
          {playing && !playing.youtubeId && (
            <iframe className="h-[min(80vh,720px)] w-full" src={`https://www.instagram.com/reel/${reelCode(playing.instagramUrl)}/embed`} title={playing.title} allow="autoplay; encrypted-media; picture-in-picture" allowFullScreen data-testid="film-instagram-embed" />
          )}
        </DialogContent>
      </Dialog>
    </section>
  );
};
